/**
 * @author        patrick@zumper.com (Patrick Giddings)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   Wrapper around Footer with tracking callbacks
 */

import { useGATrack } from '@zcode/nr-utils'
import { Footer, type FooterProps } from './Footer'

export interface FooterWithTrackingProps
  extends Omit<
    FooterProps,
    | 'onAboutClick'
    | 'onAdvertiseClick'
    | 'onAndroidClick'
    | 'onBlogClick'
    | 'onCareersClick'
    | 'onContactClick'
    | 'onFacebookClick'
    | 'onFeedbackClick'
    | 'onGuidesClick'
    | 'onHelpCenterClick'
    | 'onIOSClick'
    | 'onInstagramClick'
    | 'onLandlordResourcesClick'
    | 'onListPropertyClick'
    | 'onPinterestClick'
    | 'onPressClick'
    | 'onPrivacyClick'
    | 'onRecruitClick'
    | 'onSitemapClick'
    | 'onTOSClick'
    | 'onTenantScreeningClick'
    | 'onTwitterClick'
  > {
  analyticChannel: string
}

export const FooterWithTracking = ({
  analyticChannel,
  ...forwardProps
}: FooterWithTrackingProps) => {
  const onFacebookClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Facebook')
  const onTwitterClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Twitter')
  const onInstagramClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Instagram')
  const onPinterestClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Pinterest')
  const onAboutClick = useGATrack(analyticChannel, 'FooterLinkClick', 'About')
  const onCareersClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Careers')
  const onBlogClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Blog')
  const onGuidesClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Guides')
  const onPressClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Press')
  const onContactClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Contact')
  const onHelpCenterClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Help Center')
  const onTOSClick = useGATrack(analyticChannel, 'FooterLinkClick', 'TOS')
  const onPrivacyClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Privacy')
  const onSitemapClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Sitemap')
  const onFeedbackClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Feedback')
  const onTenantScreeningClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Tenant Screening')
  const onListPropertyClick = useGATrack(analyticChannel, 'FooterLinkClick', 'List Property')
  const onLandlordResourcesClick = useGATrack(
    analyticChannel,
    'FooterLinkClick',
    'Landlord Resources'
  )
  const onIOSClick = useGATrack(analyticChannel, 'FooterLinkClick', 'iOS')
  const onAndroidClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Android')
  const onRecruitClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Recruit')
  const onAdvertiseClick = useGATrack(analyticChannel, 'FooterLinkClick', 'Advertise')
  return (
    <Footer
      {...forwardProps}
      onAboutClick={onAboutClick}
      onAdvertiseClick={onAdvertiseClick}
      onAndroidClick={onAndroidClick}
      onBlogClick={onBlogClick}
      onCareersClick={onCareersClick}
      onContactClick={onContactClick}
      onFacebookClick={onFacebookClick}
      onFeedbackClick={onFeedbackClick}
      onGuidesClick={onGuidesClick}
      onHelpCenterClick={onHelpCenterClick}
      onIOSClick={onIOSClick}
      onInstagramClick={onInstagramClick}
      onLandlordResourcesClick={onLandlordResourcesClick}
      onListPropertyClick={onListPropertyClick}
      onPinterestClick={onPinterestClick}
      onPressClick={onPressClick}
      onPrivacyClick={onPrivacyClick}
      onRecruitClick={onRecruitClick}
      onSitemapClick={onSitemapClick}
      onTOSClick={onTOSClick}
      onTenantScreeningClick={onTenantScreeningClick}
      onTwitterClick={onTwitterClick}
    />
  )
}
